import { requestGet as wsRequestGet, requestPost as wsRequestPost } from './_wsApiFetchData';
import determineFieldType from '../common/determineFieldType';

/**
 * **Left exactly as it was, on purpose.** `Page: 1, Count: 25` are hardcoded, so
 * the page and size this is handed are thrown away — which is why the old
 * admin's provider pager does nothing and its list is silently capped at 25.
 *
 * The endpoint has taken both as query parameters all along, so the one-line fix
 * is obvious and it is deliberately not made here. `Providers.js` already passes
 * `skipPages` and `resultsCount`: repairing this function would make a dead
 * control on a screen the whole company uses start working, unasked, as a side
 * effect of a change to a different admin. That is a decision, not a bug fix,
 * and it is Claudio's.
 *
 * `/next` calls `searchProvidersPaged` below instead, so this request stays
 * byte-for-byte what it has always been.
 */
export async function searchProvider(options, language, timeZone, cancelToken) {
  const { filters } = options;
  const { type, category, name, providerId } = filters;

  const params = {
    Page: 1,
    Count: 25,
    Name: name,
    Category: category,
    Type: type,
    Id: providerId || undefined
  };

  return toPascal(await wsRequestGet('/admin/providers/search', language, timeZone, params, cancelToken));
}

/**
 * The same search, actually paged. Used only by `/next`.
 *
 * A second function rather than a parameter on the first: a default argument
 * still leaves one function serving two admins, and the guarantee worth having
 * here is that nothing the old admin sends can change because of work in this
 * tree. Eight duplicated lines buy that outright.
 */
export async function searchProvidersPaged(options, language, timeZone, cancelToken) {
  const { filters, skipPages, resultsCount } = options;
  const { type, category, name, providerId } = filters;

  const params = {
    Page: skipPages || 1,
    Count: resultsCount || 15,
    Name: name,
    Category: category,
    Type: type,
    Id: providerId || undefined
  };

  return toPascal(await wsRequestGet('/admin/providers/search', language, timeZone, params, cancelToken));
}

export async function getProvider(id, language, timeZone, cancelToken) {
  return toPascal(await wsRequestGet(`/admin/providers/get/${id}`, language, timeZone, null, cancelToken));
}

export async function editProviderData(
  {
    Id,
    Name,
    Description,
    ContactName,
    PhoneNumber,
    Email,
    Type,
    Category,
    CreatedAt,
    CurrencyIsoCode,
    IsPublic
  },
  language,
  timeZone,
  cancelToken
) {
  return toPascal(await wsRequestPost('/admin/providers/edit', language, timeZone, {
    Id,
    Name,
    Description,
    Contact: {
      Name: ContactName,
      PhoneNumber: {
        Number: PhoneNumber
      },
      EmailAddress: {
        Email
      }
    },
    Type,
    Category: Category.join(','),
    CreatedAt,
    CurrencyIsoCode,
    IsPublic
  }, null, cancelToken));
}

export async function saveNewProviderData(
  {
    Name,
    Description,
    ContactName,
    PhoneNumber,
    Email,
    Type,
    Category,
    CurrencyIsoCode,
    IsPublic
  },
  language,
  timeZone,
  cancelToken
) {
  return toPascal(await wsRequestPost('/admin/providers/create', language, timeZone, {
    Name,
    Description,
    Contact: {
      Name: ContactName,
      PhoneNumber: {
        Number: PhoneNumber
      },
      EmailAddress: {
        Email
      }
    },
    Type,
    Category: Category.join(','),
    CurrencyIsoCode,
    IsPublic
  }, null, cancelToken));
}

export async function lookupByNameOrId(key, language, timeZone, cancelToken) {
  if (!key) {
    return [];
  }

  const fieldName = await determineFieldType(key);
  const supportedFields = ['Id', 'Name'];

  if (supportedFields.indexOf(fieldName) < 0) {
    return [];
  }

  if (fieldName === 'Id') {
    const { Id, Name } = await getProvider(key, language, timeZone, cancelToken);

    return [{
      label: `${Name}`,
      value: Id
    }];
  } if (fieldName === 'Name') {
    const options = {
      resultsCount: 5,
      filters: {
        name: key
      }
    };

    const results = await searchProvider(options, language, timeZone, cancelToken);

    return results.map(({ Id, Name }) => ({
      label: `${Name}`,
      value: Id
    }));
  }
}


function toPascal(o) {
  var newO, origKey, newKey, value
  if (o instanceof Array) {
    return o.map(function(value) {
        if (typeof value === "object") {
          value = toPascal(value)
        }
        return value
    })
  } else {
    newO = {}
    for (origKey in o) {
      if (o.hasOwnProperty(origKey)) {
        newKey = (origKey.charAt(0).toUpperCase() + origKey.slice(1) || origKey).toString()
        value = o[origKey]
        if (value instanceof Array || (value !== null && value.constructor === Object)) {
          value = toPascal(value)
        }
        newO[newKey] = value
      }
    }
  }
  return newO
}
